import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { DbConnection, User, EventContext } from '../../db/index';

// Local extension of User with id for scene visualization
interface UserWithId extends User {
  id: number;
}

// Forest scene component that works with SpacetimeDB
export class FactoryScene extends HTMLElement {
  private canvas: HTMLCanvasElement;
  private scene: THREE.Scene | null;
  private camera: THREE.PerspectiveCamera | null;
  private renderer: THREE.WebGLRenderer | null;
  private controls: OrbitControls | null;
  private dbConnection: DbConnection | null;
  private entityMeshes: Map<number, THREE.Mesh>;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    // Create styles
    const style = document.createElement('style');
    style.textContent = `
      :host {
        display: block;
        width: 100%;
        height: 100%;
      }
      canvas {
        display: block;
        width: 100%;
        height: 100%;
      }
    `;
    
    // Create canvas for Three.js
    this.canvas = document.createElement('canvas');
    
    // Add to shadow DOM
    this.shadowRoot!.appendChild(style);
    this.shadowRoot!.appendChild(this.canvas);
    
    // Scene setup properties
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.dbConnection = null;
    
    // Initialize mesh maps
    this.entityMeshes = new Map();
  }
  
  connectedCallback(): void {
    this.initScene();
    this.animateScene();
    window.addEventListener('resize', this.handleResize.bind(this));
  }
  
  disconnectedCallback(): void {
    window.removeEventListener('resize', this.handleResize.bind(this));
    if (this.renderer) {
      this.renderer.dispose();
    }
  }
  
  // Set up the Three.js scene
  initScene(): void {
    // Create scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x88ccee);
    this.scene.fog = new THREE.FogExp2(0x88ccee, 0.002);
    
    // Create camera
    this.camera = new THREE.PerspectiveCamera(
      75, 
      this.canvas.clientWidth / this.canvas.clientHeight, 
      0.1, 
      1000
    );
    this.camera.position.set(5, 5, 10);
    
    // Create renderer
    this.renderer = new THREE.WebGLRenderer({ 
      canvas: this.canvas,
      antialias: true 
    });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(this.canvas.clientWidth, this.canvas.clientHeight);
    
    // Add controls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    
    // Add lights
    const ambientLight = new THREE.AmbientLight(0x404040, 3);
    this.scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.5);
    directionalLight.position.set(1, 1, 1);
    this.scene.add(directionalLight);
    
    // Add a grid for reference
    const grid = new THREE.GridHelper(100, 100);
    this.scene.add(grid);
    
    // Adjust renderer and camera when window resizes
    this.handleResize();
  }
  
  // Animation loop
  animateScene(): void {
    requestAnimationFrame(this.animateScene.bind(this));
    
    // Update controls
    if (this.controls) {
      this.controls.update();
    }
    
    // Render scene
    if (this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }
  }
  
  // Handle window resize
  handleResize(): void {
    if (!this.camera || !this.renderer) return;
    
    // Get new size from parent element
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    
    // Update camera
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    
    // Update renderer
    this.renderer.setSize(width, height, false);
  }
  
  // Connect to SpacetimeDB
  connectToSpacetimeDB(connection: DbConnection): void {
    this.dbConnection = connection;
    
    // Subscribe to entity updates
    this.setupSubscriptions();
  }
  
  // Set up SpacetimeDB subscriptions
  setupSubscriptions(): void {
    if (!this.dbConnection) return;
    
    // Subscribe to User table changes
    this.dbConnection.db.user.onInsert((_ctx: EventContext, user: User) => {
      const userWithId = { ...user, id: Math.floor(Math.random() * 1000) } as UserWithId;
      console.log('User added:', userWithId);
      this.addUserToScene(userWithId);
    });
      
    this.dbConnection.db.user.onDelete((_ctx: EventContext, user: User) => {
      console.log('User removed:', user);
      // Find the user in entityMeshes by identity
      const userId = this.findUserIdByIdentity(user.identity);
      if (userId !== null) {
        this.removeUserFromScene({ ...user, id: userId } as UserWithId);
      }
    });
    
    this.dbConnection.db.user.onUpdate((_ctx: EventContext, oldUser: User, newUser: User) => {
      console.log('User updated:', oldUser, newUser);
      const userId = this.findUserIdByIdentity(newUser.identity);
      if (userId !== null) {
        this.updateUserInScene({ ...newUser, id: userId } as UserWithId);
      }
    });
  }
  
  // Helper method to find a user ID by identity
  private findUserIdByIdentity(_identity: any): number | null {
    for (const [id, _mesh] of this.entityMeshes.entries()) {
      // Here we would typically use mesh.userData to store identity
      // This is a simplified example
      return id;
    }
    return null;
  }
  
  // Add a user entity to the scene
  addUserToScene(user: UserWithId): void {
    if (!this.scene) return;
    
    // Create a simple mesh for the user
    const geometry = new THREE.BoxGeometry(1, 2, 1);
    const material = new THREE.MeshLambertMaterial({ 
      color: user.online ? 0x00ff00 : 0xff0000 
    });
    const mesh = new THREE.Mesh(geometry, material);
    
    // Store user identity in mesh userData
    mesh.userData = { identity: user.identity };
    
    // Position the mesh based on user data
    mesh.position.set(
      (user.id % 10) * 2, // Simple positioning based on ID
      1,
      Math.floor(user.id / 10) * 2
    );
    
    // Add to scene and store in map
    this.scene.add(mesh);
    this.entityMeshes.set(user.id, mesh);
  }
  
  // Update a user entity in the scene
  updateUserInScene(user: UserWithId): void {
    const mesh = this.entityMeshes.get(user.id);
    
    if (mesh && mesh.material instanceof THREE.MeshLambertMaterial) {
      // Update mesh color based on online status
      mesh.material.color.set(user.online ? 0x00ff00 : 0xff0000);
    }
  }
  
  // Remove a user entity from the scene
  removeUserFromScene(user: UserWithId): void {
    if (!this.scene) return;
    
    const mesh = this.entityMeshes.get(user.id);
    
    if (mesh) {
      this.scene.remove(mesh);
      this.entityMeshes.delete(user.id);
    }
  }
}

// Register the custom element
customElements.define('factory-scene', FactoryScene); 